import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  BellOff,
  Building2,
  Check,
  ChevronDown,
  ChevronsUpDown,
  Download,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MessageSquare,
  Play,
  Settings as SettingsIcon,
  UserRound,
  Users,
  Volume2,
  VolumeX,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useTenant } from "@/context/TenantContext";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBarActions";
import { MESSAGES_CHANGED, SUPPORT_CHANGED, onChange } from "@/lib/events";
import { noteVisit } from "@/lib/lastPage";
import { useInstallPrompt } from "@/lib/useInstallPrompt";
import { enablePush, disablePush, pushPermission, pushSupported } from "@/lib/push";
import { updateAppBadge } from "@/lib/badge";
import {
  SOUNDS,
  isSoundOn,
  setSoundOn as persistSoundOn,
  getSoundChoice,
  setSoundChoice as persistSoundChoice,
  playSound,
  playSelectedSound,
} from "@/lib/sound";
import { cn } from "@/lib/utils";


/**
 * Two counts in one place: the filled one is what's waiting on you, the
 * outlined one is what's already in hand. A single count that included both
 * stayed lit after you'd replied, which taught you to ignore it.
 */
function NavBadge({ action = 0, pending = 0 }) {
  if (!action && !pending) return null;
  return (
    <span className="ml-auto flex items-center gap-1 shrink-0">
      {action > 0 && (
        <span
          title={`${action} waiting on a reply`}
          className="bg-brand text-white text-[10.5px] font-mono rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
        >
          {action > 9 ? "9+" : action}
        </span>
      )}
      {pending > 0 && (
        <span
          title={`${pending} in progress`}
          className="border border-line text-muted2 text-[10.5px] font-mono rounded-full px-1.5 py-[1px] min-w-[18px] text-center"
        >
          {pending > 9 ? "9+" : pending}
        </span>
      )}
    </span>
  );
}

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/messages", label: "Messages", icon: MessageSquare, badge: "messages" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
  { to: "/team", label: "Team", icon: Users, adminOnly: true },
  { to: "/support", label: "Support", icon: LifeBuoy, badge: "support" },
  // Everyone gets this, including agents — Settings is admin-only, and it's
  // where someone would otherwise look for their own password.
  { to: "/profile", label: "Your profile", icon: UserRound },
];

/**
 * For the owner this is a switcher — which client am I in, and jump to another.
 * For client agents it's just their business name, since they only have one.
 */
function ClientBadge({ onNavigate }) {
  const { isOwner, tenants, tenantId, setTenantId, activeTenant } = useTenant();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const name = activeTenant?.name || settings.business_name || "Workspace";
  const initial = (name || "?")[0].toUpperCase();
  const isWorkspace = !!activeTenant?.is_workspace;

  const badge = (
    <>
      <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center shrink-0 shadow-sm">
        <span className="font-display text-white text-[15px] font-semibold leading-none">
          {initial}
        </span>
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="font-display text-[16px] font-semibold tracking-[-0.01em] text-ink leading-none truncate">
          {name}
        </div>
        <div className="eyebrow mt-1 leading-none">
          {isWorkspace ? "Your sandbox" : isOwner ? "Client" : "Business"}
        </div>
      </div>
    </>
  );

  if (!isOwner) {
    return <div className="flex items-center gap-2.5 w-full">{badge}</div>;
  }

  const switchTo = (id) => {
    setTenantId(id);
    setOpen(false);
    onNavigate?.();
    navigate("/dashboard");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2.5 w-full rounded-xl p-1 -m-1 hover:bg-brand-light/50 transition-colors">
          {badge}
          <ChevronsUpDown className="w-4 h-4 text-muted2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[232px] p-0" align="start">
        <div className="px-3 py-2 border-b border-line">
          <div className="eyebrow">Switch client</div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {tenants.map((t) => (
            <button
              key={t.tenant_id}
              onClick={() => switchTo(t.tenant_id)}
              className={cn(
                "w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-brand-light/60 transition-colors",
                t.tenant_id === tenantId && "bg-brand-light"
              )}
            >
              <Check
                className={cn(
                  "w-4 h-4 text-brand shrink-0",
                  t.tenant_id === tenantId ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="text-[13px] text-ink truncate flex-1">{t.name}</span>
              <span className="font-mono text-[11px] text-muted2 shrink-0">{t.conversation_count}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-line p-1">
          <Link
            to="/overview"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-muted2 hover:bg-brand-light/60 hover:text-ink transition-colors"
          >
            <Building2 className="w-4 h-4" />
            Platform overview
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children, title, subtitle, action, fullBleed = false }) {
  const { user, logout } = useAuth();
  const { isOwner, setTenantId } = useTenant();
  const { isOn, labelFor, loaded } = useSettings();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [support, setSupport] = useState({ unread: 0, open: 0 });
  const [messages, setMessages] = useState({ unread: 0, total: 0 });
  // Baselines for the sound trigger below — null until the first poll of
  // each has actually landed, so whatever's already unread when the app
  // loads doesn't play a sound; only a rise after that does.
  const prevMessagesUnreadRef = useRef(null);
  const prevSupportUnreadRef = useRef(null);
  const [soundOn, setSoundOnState] = useState(() => isSoundOn());
  const [soundChoice, setSoundChoiceState] = useState(() => getSoundChoice());
  const [soundPickerOpen, setSoundPickerOpen] = useState(false);

  // Remember where they've been, so a support report can suggest the page it's
  // probably about.
  useEffect(() => {
    noteVisit(pathname);
  }, [pathname]);

  // A reply is easy to miss, so unread answers show as a count on the tab.
  useEffect(() => {
    let cancelled = false;
    const check = () =>
      api
        .get("/support/mine/summary")
        .then(({ data }) => {
          if (cancelled) return;
          const unread = data.unread || 0;
          // Compare against the last count seen, not just "> 0" — otherwise
          // a reply that's been sitting unread for a while would replay the
          // sound on every single poll instead of once when it arrives.
          if (prevSupportUnreadRef.current !== null && unread > prevSupportUnreadRef.current) {
            playSelectedSound();
          }
          prevSupportUnreadRef.current = unread;
          setSupport({ unread, open: data.open || 0 });
        })
        .catch(() => {});
    check();
    const timer = setInterval(check, 20000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    const off = onChange(SUPPORT_CHANGED, check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      off();
    };
  }, [pathname]);

  // Same idea for unread conversations — checked more often since a chat
  // reply is expected sooner than a support reply.
  useEffect(() => {
    let cancelled = false;
    const check = () =>
      api
        .get("/messaging/summary")
        .then(({ data }) => {
          if (cancelled) return;
          const unread = data.unread || 0;
          if (prevMessagesUnreadRef.current !== null && unread > prevMessagesUnreadRef.current) {
            playSelectedSound();
          }
          prevMessagesUnreadRef.current = unread;
          setMessages({ unread, total: data.total || 0 });
        })
        .catch(() => {});
    check();
    // Same push-driven bump as Messages.jsx's poll — MESSAGES_CHANGED fires
    // the moment a push notification lands in this tab, so the sidebar badge
    // updates immediately rather than waiting out this interval, which is
    // just the fallback for anyone without notifications turned on.
    const timer = setInterval(check, 3000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    const off = onChange(MESSAGES_CHANGED, check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      off();
    };
  }, [pathname]);

  // Taskbar/dock badge — the count on RoamNote's own icon once it's
  // installed as an app (see lib/badge.js). Both unread buckets count here,
  // same as the two feed the sidebar's own badges above; setAppBadge is a
  // no-op wherever it isn't supported, so this is harmless to run always
  // rather than only for people who happen to have it installed.
  useEffect(() => {
    updateAppBadge(messages.unread + support.unread);
  }, [messages.unread, support.unread]);

  // "Online" for the app as a whole, not just while Messages is open — a
  // periodic ping plus one on focus, so stepping away and coming back marks
  // you online again quickly rather than waiting out the interval.
  useEffect(() => {
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const timer = setInterval(ping, 25000);
    const onFocus = () => ping();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const canAdmin = ["owner", "admin"].includes(user?.role);

  // Install prompt and push notifications both live in the sidebar footer,
  // pinned below the nav on every screen size since the sidebar markup
  // itself (`sidebar` below) is shared by the desktop <aside> and the mobile
  // drawer. The install button always shows (until the app is actually
  // installed) — canInstall only covers Chrome/Edge's one-click native
  // prompt, which doesn't fire reliably enough to gate visibility on; when
  // it isn't available, the same button shows browser-specific manual steps
  // instead of just disappearing (see useInstallPrompt's own note).
  const { canInstall, installed, promptInstall, instructions } = useInstallPrompt();

  const handleInstallClick = async () => {
    if (canInstall) {
      await promptInstall();
    } else {
      toast.message("To install this app", { description: instructions, duration: 8000 });
    }
  };

  const [notifPermission, setNotifPermission] = useState(() => pushPermission());
  const [notifBusy, setNotifBusy] = useState(false);
  const notifOn = notifPermission === "granted";

  const toggleNotifications = async () => {
    if (notifBusy) return;
    setNotifBusy(true);
    try {
      if (notifOn) {
        await disablePush();
        setNotifPermission("default");
        toast.success("Notifications turned off");
      } else {
        const result = await enablePush();
        setNotifPermission(result);
        if (result === "granted") toast.success("Notifications turned on");
        else if (result === "denied")
          toast.error("Notifications are blocked — allow them for this site in your browser settings");
      }
    } catch {
      toast.error("Couldn't change notification settings");
    } finally {
      setNotifBusy(false);
    }
  };

  // Independent of the pop-up toggle above — no browser permission to ask
  // for, so this is just a preference (see lib/sound.js). The trigger below
  // uses PopoverAnchor rather than PopoverTrigger on purpose: PopoverTrigger
  // wires its own click-to-toggle handler onto the element, which raced this
  // one (both firing on the same click, sometimes disagreeing on the result).
  // PopoverAnchor only positions the popover — clicking the button is fully
  // this handler's job, while Radix's own outside-click/Escape dismissal
  // still closes the picker (via onOpenChange below) without that also
  // turning sound off, so browsing elsewhere doesn't silently disable it.
  const toggleSound = () => {
    const next = !soundOn;
    setSoundOnState(next);
    persistSoundOn(next);
    setSoundPickerOpen(next);
    if (next) playSound(soundChoice);
  };

  const chooseSound = (name) => {
    setSoundChoiceState(name);
    persistSoundChoice(name);
    playSound(name);
  };

  const doLogout = async () => {
    // Whoever opens this installed app next (same computer, shared machine
    // or otherwise) shouldn't see a leftover count from an account they
    // aren't signed into.
    updateAppBadge(0);
    setTenantId(null);
    await logout();
    navigate("/login");
  };

  // A switched-off feature leaves the menu entirely, and an optional one can
  // carry the client's own word for it.
  //
  // Until settings are known, optional items are held back rather than assumed
  // on — showing a tab and then removing it looks like a glitch, and briefly
  // advertises a feature the client has switched off.
  const items = NAV.filter((n) => {
    if (n.adminOnly && !canAdmin) return false;
    if (!n.module) return true;
    return loaded && isOn(n.module);
  }).map((n) => (n.module && !n.gateOnly ? { ...n, label: labelFor(n.module) } : n));

  const sidebar = (
    <>
      <div className="h-[68px] flex items-center px-4 border-b border-line">
        <ClientBadge onNavigate={() => setOpen(false)} />
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {items.map((n) => {
          const active = pathname === n.to || pathname.startsWith(`${n.to}/`);
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors",
                active
                  ? "bg-brand-light text-brand"
                  : "text-muted2 hover:bg-brand-light/50 hover:text-ink"
              )}
            >
              <n.icon className="w-[17px] h-[17px] shrink-0" />
              {n.label}
              {n.badge === "support" && (
                // Filled: replies they haven't read. Outlined: still open, but
                // the ball is with support.
                <NavBadge
                  action={support.unread}
                  pending={Math.max(support.open - support.unread, 0)}
                />
              )}
              {n.badge === "messages" && <NavBadge action={messages.unread} />}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-line">
        {pushSupported() && (
          <button
            onClick={toggleNotifications}
            disabled={notifBusy || notifPermission === "denied"}
            title={
              notifPermission === "denied"
                ? "Blocked — allow notifications for this site in your browser settings"
                : notifOn
                ? "Turn off notifications"
                : "Get notified about new messages"
            }
            className="flex items-center gap-3 w-full px-3.5 py-2.5 mb-1 rounded-xl text-[13.5px] font-medium text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {notifOn ? (
              <Bell className="w-[17px] h-[17px] shrink-0 text-brand" />
            ) : (
              <BellOff className="w-[17px] h-[17px] shrink-0" />
            )}
            {notifPermission === "denied"
              ? "Notifications blocked"
              : notifOn
              ? "Notifications on"
              : "Turn on notifications"}
          </button>
        )}
        <Popover
          open={soundPickerOpen}
          onOpenChange={(next) => {
            // Only ever asked to close (outside click / Escape) — a request
            // to open would have to come through the anchor's own onClick
            // instead, since PopoverAnchor doesn't generate one itself.
            if (!next) setSoundPickerOpen(false);
          }}
        >
          <PopoverAnchor asChild>
            <button
              onClick={toggleSound}
              title={soundOn ? "Turn off sound" : "Play a sound for new messages"}
              className={cn(
                "flex items-center gap-3 w-full px-3.5 py-2.5 mb-1 rounded-xl text-[13.5px] font-medium transition-colors",
                soundOn
                  ? "text-ink bg-brand-light/40 hover:bg-brand-light/60"
                  : "text-muted2 hover:bg-brand-light/50 hover:text-ink"
              )}
            >
              {soundOn ? (
                <Volume2 className="w-[17px] h-[17px] shrink-0 text-brand" />
              ) : (
                <VolumeX className="w-[17px] h-[17px] shrink-0" />
              )}
              {soundOn ? "Sound on" : "Turn on sound"}
              <ChevronDown
                className={cn(
                  "w-3.5 h-3.5 ml-auto shrink-0 text-muted2 transition-transform",
                  soundPickerOpen && "rotate-180 text-brand"
                )}
              />
            </button>
          </PopoverAnchor>
          <PopoverContent className="w-64 p-2" align="end" side="right">
            <div className="eyebrow px-1.5 pb-1.5">Sound</div>
            <div className="space-y-0.5">
              {SOUNDS.map((s) => {
                const isSelected = s.name === soundChoice;
                return (
                  <div
                    key={s.name}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg pr-1.5 transition-colors",
                      isSelected ? "bg-brand-light" : "hover:bg-brand-light/50"
                    )}
                  >
                    <button
                      onClick={() => chooseSound(s.name)}
                      className="flex-1 min-w-0 flex items-center gap-2.5 px-2 py-1.5 text-left"
                    >
                      <span
                        className={cn(
                          "w-[15px] h-[15px] rounded-full border shrink-0 flex items-center justify-center",
                          isSelected ? "border-brand" : "border-line"
                        )}
                      >
                        {isSelected && <span className="w-[8px] h-[8px] rounded-full bg-brand" />}
                      </span>
                      <span
                        className={cn(
                          "text-[12.5px] truncate",
                          isSelected ? "font-semibold text-brand-hover" : "font-medium text-ink"
                        )}
                      >
                        {s.name}
                      </span>
                    </button>
                    <button
                      onClick={() => playSound(s.name)}
                      aria-label={`Preview ${s.name}`}
                      title={`Preview ${s.name}`}
                      className="w-6 h-6 rounded-full border border-line bg-card flex items-center justify-center text-muted2 hover:border-brand hover:text-brand transition-colors shrink-0"
                    >
                      <Play className="w-2.5 h-2.5" fill="currentColor" />
                    </button>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {!installed && (
          <button
            onClick={handleInstallClick}
            title={canInstall ? "Install this app" : instructions}
            className="flex items-center gap-3 w-full px-3.5 py-2.5 mb-1 rounded-xl text-[13.5px] font-medium text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors"
          >
            <Download className="w-[17px] h-[17px] shrink-0" />
            Install app
          </button>
        )}
        {isOwner && (
          <Link
            to="/accounts"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-3.5 py-2.5 mb-1 rounded-xl text-[13.5px] font-medium text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors"
          >
            <Building2 className="w-[17px] h-[17px] shrink-0" />
            All accounts
          </Link>
        )}
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[13px] shrink-0 hover:ring-2 hover:ring-brand-ring transition-all"
            title="Your profile"
          >
            {(user?.name?.[0] || "U").toUpperCase()}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-ink truncate">{user?.name}</div>
            <div className="text-[11.5px] text-muted2 capitalize">
              {user?.role === "owner" ? "Platform owner" : user?.role}
            </div>
          </div>
          <button
            onClick={doLogout}
            title="Sign out"
            className="text-muted2 hover:text-stock-out transition-colors p-1.5 rounded-lg hover:bg-rose-50"
          >
            <LogOut className="w-[17px] h-[17px]" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[248px] bg-card border-r border-line flex-col fixed h-screen z-30">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-ink/30 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-[264px] bg-card border-r border-line flex flex-col z-50 animate-rise">
            {sidebar}
          </aside>
        </>
      )}

      <div className="lg:ml-[248px] min-w-0">
        <TopBar
          title={title}
          subtitle={subtitle}
          action={action}
          onMenu={() => setOpen((v) => !v)}
          menuOpen={open}
          scope="tenant"
          searchPlaceholder="Search messages…"
        />
        {/*
          Capped so a wide monitor doesn't stretch a table row until the item
          name and its status sit a foot apart. Wider than most content needs,
          so nothing feels cramped on a laptop. fullBleed opts out of both the
          cap and all the padding at lg: — for a page like Messages that wants
          its panel to run flush against the sidebar and the window's top,
          right and bottom edges, not sit inside a visibly separate card with
          a gutter around it. Below lg: it keeps a small gutter (px-3 py-3) —
          the layout there is a natural stack, not one panel filling the
          screen, so a bare edge-to-edge look wouldn't read the same way.
        */}
        <main
          className={cn(
            "min-w-0",
            fullBleed
              ? "px-3 sm:px-4 py-3 lg:p-0 lg:h-[calc(100vh-68px)] lg:flex lg:flex-col"
              : "px-4 sm:px-7 py-6 pb-16"
          )}
        >
          <div className={fullBleed ? "lg:flex-1 lg:min-h-0 lg:flex lg:flex-col" : "mx-auto w-full max-w-[1500px]"}>
            {children}
          </div>
        </main>
      </div>

    </div>
  );
}
